import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { asset } from '../utils/asset';
import { useTranslation } from '../hooks/useTranslation';
import { useKosik } from '../contexts/KosikContext';
import LanguageSwitcher from './LanguageSwitcher';
import './ModernHeader.css';


type Section = 'kameny' | 'sperky' | 'encyklopedie' | 'kosik' | null;

interface SectionConfig {
  id: Section;
  label: string;
  color: string;
  route: string;
}

interface ModernHeaderProps {
  sectionImages?: Record<string, string>;
}

const ModernHeader: React.FC<ModernHeaderProps> = ({ sectionImages }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { itemCount } = useKosik();
  
  // Definice sekcí s překladitelnými názvy
  const sections: SectionConfig[] = [
    { 
      id: 'kameny', 
      label: t('header.stones'), 
      color: '#7B68EE', // Medium Slate Blue - královsky fialová pro drahokamy
      route: '/objekty?typ=kamen'
    },
    { 
      id: 'sperky', 
      label: t('header.jewelry'), 
      color: '#DAA520', // Goldenrod - zlatá pro šperky
      route: '/objekty?typ=sperk'
    },
    { 
      id: 'encyklopedie', 
      label: t('header.encyclopedia'), 
      color: '#2E8B57', // Sea Green - zelená pro znalosti
      route: '/encyklopedie'
    },
  ];
  
  // Subtitle text
  const subtitleText = `${t('header.stones').toLowerCase()} a ${t('header.jewelry').toLowerCase()}`;
  
  // Určit aktivní sekci podle URL (memoizováno, aby eslint nezobrazoval missing dependency)
  const getActiveSectionFromPath = useCallback((): Section => {
    const path = location.pathname;
    const search = location.search;

    if (path === '/') return null; // Home page - no active section
    if (path === '/kosik') return 'kosik' as Section; // Cart page
    if (path.startsWith('/encyklopedie')) return 'encyklopedie';
    if (search.includes('typ=kamen')) return 'kameny';
    if (search.includes('typ=sperk')) return 'sperky';

    // Default for other pages
    return null;
  }, [location.pathname, location.search]);

  const [activeSection, setActiveSection] = useState<Section>(getActiveSectionFromPath());

  // Update active section when location changes
  useEffect(() => {
    setActiveSection(getActiveSectionFromPath());
  }, [getActiveSectionFromPath]);

  // Update document title based on current route/section
  useEffect(() => {
    let title = 'ZR - kameny a šperky';

    if (location.pathname === '/kosik') {
      title = 'ZR - košík';
    } else if (location.pathname.startsWith('/encyklopedie')) {
      title = 'ZR - Lapidář český';
    } else if (location.search.includes('typ=kamen')) {
      title = 'ZR - kameny';
    } else if (location.search.includes('typ=sperk')) {
      title = 'ZR - šperky';
    }

    document.title = title;
  }, [location.pathname, location.search]);

  const handleSectionClick = (section: SectionConfig) => {
    setActiveSection(section.id);
    navigate(section.route);
  };

  const handleCartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/kosik');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const isCartPage = location.pathname === '/kosik';
  const activeConfig = (activeSection && activeSection !== 'kosik') ? sections.find(s => s.id === activeSection) : null;
  // Vždy zobrazit všechny sekce, ale označit aktivní
  const allSections = sections;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Expose header height as a CSS variable so other components (sidebar)
  // can position themselves correctly below the sticky header.
  useEffect(() => {
    const setHeaderVar = () => {
      const height = wrapperRef.current ? wrapperRef.current.offsetHeight : 0;
      document.documentElement.style.setProperty('--header-sticky-offset', `${height}px`);
    };

    // initial set
    setHeaderVar();

    // update on resize
    window.addEventListener('resize', setHeaderVar);
    return () => {
      window.removeEventListener('resize', setHeaderVar);
      // cleanup variable
      document.documentElement.style.setProperty('--header-sticky-offset', `0px`);
    };
  }, []);

  // Handle scroll to hide/show header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        // Always show header at the top
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current) {
        // Scrolling down - hide header
        setIsHeaderVisible(false);
      } else {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div 
      className={`modern-header-wrapper ${!isHeaderVisible ? 'header-hidden' : ''}`} 
      ref={wrapperRef}
    >
      <header className="modern-header">
        {/* Hlavní aktivní sekce - zobrazí se pouze pokud je vybrána sekce */}
        {activeConfig && !isCartPage && (
          <div 
            className="active-section"
            style={{
              background: `linear-gradient(135deg, ${activeConfig.color} 0%, ${adjustBrightness(activeConfig.color, -20)} 100%)`,
            }}
          >
            <div className="active-section-content">
              {/* Wrapper pro textový obsah */}
              <div className="content-text-wrapper">
                <img
                  src={asset('/images/ZR_logo.png')}
                  alt="Logo"
                  className="logo-image"
                  width="108"
                  height="65"
                  loading="eager"
                  decoding="async"
                  onClick={handleLogoClick}
                  style={{
                    width: '108px',
                    height: '65px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                    flexShrink: 0,
                    cursor: 'pointer',
                    margin: '0 -20px'
                  }}
                />
                <div className="title-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <h1 className="title" style={{ marginBottom: 0 }}>Ing. {t('common.authorName')}</h1>
                    <span className="subtitle">{t('header.stones').toLowerCase()} a {t('header.jewelry').toLowerCase()}</span>
                  </div>
                </div>
                
                <div className="active-section-label">
                  {activeConfig.label}
                </div>
              </div>

              {/* Ikona košíku a přepínač jazyků */}
              <div className="header-controls-container">
                <div 
                  className={`cart-icon ${isCartPage ? 'active' : ''}`}
                  onClick={handleCartClick}
                  title={t('cart.title')}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}

        {/* Úvodní stránka header - zobrazí se když není vybrána žádná sekce */}
        {!activeConfig && !isCartPage && (
          <div 
            className="active-section"
            style={{
              background: 'linear-gradient(135deg, #2C3E50 0%, #1a252f 100%)',
            }}
          >
            <div className="active-section-content">
              {/* Wrapper pro textový obsah */}
              <div className="content-text-wrapper">
                <img
                  src={asset('/images/ZR_logo.png')}
                  alt="Logo"
                  className="logo-image"
                  width="108"
                  height="65"
                  loading="eager"
                  decoding="async"
                  style={{
                    width: '108px',
                    height: '65px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                    flexShrink: 0,
                    margin: '0 -20px'
                  }}
                />
                <div className="title-container" style={{ cursor: 'default' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <h1 className="title" style={{ marginBottom: 0 }}>Ing. {t('common.authorName')}</h1>
                    <span className="subtitle">{subtitleText}</span>
                  </div>
                </div>
              </div>

              {/* Ikona košíku a přepínač jazyků */}
              <div className="header-controls-container">
                <div 
                  className={`cart-icon ${isCartPage ? 'active' : ''}`}
                  onClick={handleCartClick}
                  title={t('cart.title')}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}

        {/* Košík header - zobrazí se na stránce košíku */}
        {isCartPage && (
          <div 
            className="active-section"
            style={{
              background: 'linear-gradient(135deg, #4A90A4 0%, #2C5F6F 100%)',
            }}
          >
            <div className="active-section-content">
              {/* Wrapper pro textový obsah */}
              <div className="content-text-wrapper">
                <img
                  src={asset('/images/ZR_logo.png')}
                  alt="Logo"
                  className="logo-image"
                  width="108"
                  height="65"
                  loading="eager"
                  decoding="async"
                  onClick={handleLogoClick}
                  style={{
                    width: '108px',
                    height: '65px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                    flexShrink: 0,
                    cursor: 'pointer',
                    margin: '0 -20px'
                  }}
                />
                <div className="title-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <h1 className="title" style={{ marginBottom: 0 }}>Ing. {t('common.authorName')}</h1>
                    <span className="subtitle">{subtitleText}</span>
                  </div>
                </div>
                
                <div className="active-section-label">
                  {t('cart.title')}
                </div>
              </div>

              {/* Ikona košíku a přepínač jazyků */}
              <div className="header-controls-container">
                <div 
                  className="cart-icon active"
                  onClick={handleCartClick}
                  title={t('cart.title')}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}

        {/* Neaktivní sekce - vždy horizontálně pod hlavní lištou */}
        <div className="inactive-sections-container">
          {allSections.map((section) => (
            <div
              key={section.id}
              className={`inactive-section ${section.id === activeSection ? 'section-active' : ''}`}
              style={{
                background: `linear-gradient(135deg, ${section.color} 0%, ${adjustBrightness(section.color, -20)} 100%)`,
              }}
              onClick={() => handleSectionClick(section)}
            >
              <span className="inactive-section-label">
                {section.label}
              </span>
            </div>
          ))}
        </div>
      </header>
    </div>
  );
};

// Pomocná funkce pro změnu jasu barvy
function adjustBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

export default memo(ModernHeader);
